// App-wide image lightbox.
//
// Why: previously every clickable image (inbound email attachment, note
// attachment, assist chat image, journey export thumbnail, inline HTML
// `<img>` inside a message bubble) opened in a new browser tab via an
// `<a target="_blank">` wrapper. That dropped the agent into a separate
// page, lost their scroll position in the thread, and on mobile usually
// triggered the OS app picker. A modal overlay keeps them in flow —
// click image → see it big, click outside / press Esc → back to where
// they were.
//
// Usage:
//   1. Mount `<LightboxProvider>` once at the app root (`App.tsx`,
//      OUTSIDE any auth gate so anon surfaces like /assist/:token can
//      use it too).
//   2. In any component: `const lightbox = useLightbox(); ...
//      onClick={() => lightbox.open(url, altText)}`.
//   3. For HTML content rendered via dangerouslySetInnerHTML (e.g. the
//      sanitised email body in `MessageBody.tsx`), attach an onClick to
//      the WRAPPING element that detects `<img>` clicks via event
//      delegation — see `handleHtmlImageClick` below for the helper.
//
// Non-image attachments (PDFs, docs, archives) are unchanged — they
// continue to download / open in a new tab via the normal anchor link.

import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Download, ExternalLink, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface LightboxState {
  src: string;
  alt: string;
  filename?: string;
}

interface LightboxContextValue {
  /** Open the lightbox with the given image src + alt text. Pass an
   *  optional `filename` to drive the Download anchor's `download=`
   *  attribute (so the saved file gets a sensible name). */
  open: (src: string, alt?: string, filename?: string) => void;
  close: () => void;
}

const LightboxContext = createContext<LightboxContextValue | null>(null);

/** Read the lightbox handle from any descendant of `<LightboxProvider>`.
 *  Returns a no-op pair if the provider is missing — useful for SSR /
 *  unit-test contexts where the provider isn't mounted; production
 *  always has it. */
export function useLightbox(): LightboxContextValue {
  return useContext(LightboxContext) ?? NO_OP;
}

const NO_OP: LightboxContextValue = {
  open: () => {},
  close: () => {},
};

export function LightboxProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<LightboxState | null>(null);

  const open = useCallback((src: string, alt?: string, filename?: string) => {
    if (!src) return;
    setState({ src, alt: alt ?? 'Image', filename });
  }, []);
  const close = useCallback(() => setState(null), []);

  return (
    <LightboxContext.Provider value={{ open, close }}>
      {children}
      <Dialog open={!!state} onOpenChange={(o) => !o && close()}>
        {state ? (
          <DialogContent
            // Radix Dialog ships with `max-w-lg` + opaque card chrome
            // by default — we override to a near-fullscreen, transparent
            // shell so the image owns the visual space. The dark
            // backdrop comes from Radix's DialogOverlay (`bg-black/80`
            // by default in our shadcn copy).
            className={cn(
              'flex max-h-[95vh] max-w-[95vw] flex-col items-center justify-center gap-0 border-0 bg-transparent p-0 shadow-none',
              // Hide the default Radix close button (top-right X with
              // border + ring); we render our own white-on-dark X in
              // the bottom action strip below for better visibility
              // over arbitrary image content.
              '[&>button:last-child]:hidden',
            )}
            onClick={(e) => {
              // Click on the empty space INSIDE the dialog (but outside
              // the image) closes — matches every image lightbox the
              // user has ever used (Twitter, Slack, Gmail). The image
              // itself is excluded via stopPropagation in its wrapper.
              if (e.target === e.currentTarget) close();
            }}
          >
            {/* Radix requires a Title; without one the dialog is announced as
                unnamed and Radix logs an error. Visually hidden because the
                image is the content — but it names the image being viewed,
                which is exactly what a screen-reader user needs on open. */}
            <DialogTitle className="sr-only">
              {state.filename || state.alt || 'Image preview'}
            </DialogTitle>
            <img
              src={state.src}
              alt={state.alt}
              // object-contain so portraits + landscapes both stay
              // inside the bounds without distortion. Click on the
              // image is consumed so it doesn't close — only clicks
              // on the surrounding empty space close.
              onClick={(e) => e.stopPropagation()}
              referrerPolicy="no-referrer"
              className="max-h-[88vh] max-w-[95vw] cursor-default rounded-md object-contain shadow-2xl"
            />
            {/* Bottom action strip — Download + Open-original + Close.
                Sits on its own row below the image so it doesn't cover
                any pixels. Translucent black so it reads over any
                background. */}
            <div className="mt-3 flex items-center gap-2 rounded-full bg-black/60 px-3 py-1.5 text-white backdrop-blur">
              {state.filename ? (
                <span className="max-w-[40vw] truncate text-2xs opacity-80">
                  {state.filename}
                </span>
              ) : null}
              <a
                href={state.src}
                download={state.filename}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                title="Download"
                className="inline-flex h-7 w-7 items-center justify-center rounded-full hover:bg-white/15"
              >
                <Download className="h-4 w-4" />
              </a>
              <a
                href={state.src}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                title="Open original in a new tab"
                className="inline-flex h-7 w-7 items-center justify-center rounded-full hover:bg-white/15"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
              <button
                type="button"
                onClick={close}
                title="Close (Esc)"
                aria-label="Close lightbox"
                className="inline-flex h-7 w-7 items-center justify-center rounded-full hover:bg-white/15"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </DialogContent>
        ) : null}
      </Dialog>
    </LightboxContext.Provider>
  );
}

/** Event-delegation helper for sanitised HTML content rendered via
 *  `dangerouslySetInnerHTML` — e.g. the email body inside
 *  `MessageBody.tsx`. Attach to the WRAPPING `<div onClick={…}>`. Any
 *  click that lands on an `<img>` (or on an `<a>` whose only child is
 *  an `<img>`) is treated as an image click: the default
 *  open-in-new-tab is prevented and the supplied `openLightbox` is
 *  invoked with the image's URL. All other clicks (text-only links,
 *  whitespace, etc.) pass through untouched.
 *
 *  Pass the result of `useLightbox()` as `openLightbox`. */
export function handleHtmlImageClick(
  event: React.MouseEvent<HTMLElement>,
  openLightbox: (src: string, alt?: string, filename?: string) => void,
): void {
  const target = event.target;
  if (!(target instanceof Element)) return;

  // Case 1: click landed directly on an <img>.
  let img: HTMLImageElement | null =
    target instanceof HTMLImageElement ? target : null;

  // Case 2: click landed on an <a> that wraps an <img> (common in our
  // outbound replies and in client-side cid: resolution — see
  // MessageBody's `splitBody` cid_image branch). If the anchor's only
  // meaningful child is an image, treat the whole click as image.
  if (!img) {
    const anchor = target.closest('a');
    if (anchor) {
      const childImg = anchor.querySelector('img');
      if (childImg instanceof HTMLImageElement) img = childImg;
    }
  }

  if (!img || !img.src) return;

  // Skip tracking pixels and bare placeholders. A 1×1 transparent gif
  // shouldn't pop a fullscreen lightbox.
  if (img.naturalWidth > 0 && img.naturalWidth < 16) return;

  event.preventDefault();
  event.stopPropagation();
  openLightbox(img.src, img.alt || 'Image');
}
